import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api, money, day, anyDay } from "./api.js";
import Icon from "./icons.jsx";
import { shortName } from "./company/common.jsx";

// Пошук у шапці (⌘K / Ctrl+K). У компанії — ПН/РК, контрагенти, МД, звітність і коди УКТЗЕД/ДКПП;
// на списку компаній — компанії. Комбобокс: ↑/↓ — вибір, Enter — перехід, Escape — закрити
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export default function GlobalSearch({ companyId, companies }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ q: "", groups: [], loading: false, error: null });
  const [active, setActive] = useState(0);
  const input = useRef(null), root = useRef(null);
  const listId = useId();
  const narrow = useNarrow();

  // ⌘K / Ctrl+K — фокус у пошук з будь-якого місця
  useEffect(() => {
    const on = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); input.current?.focus(); input.current?.select(); setOpen(true); }
    };
    addEventListener("keydown", on);
    return () => removeEventListener("keydown", on);
  }, []);
  useEffect(() => {
    const out = (e) => { if (!root.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", out);
    return () => document.removeEventListener("pointerdown", out);
  }, []);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setState({ q: t, groups: [], loading: false, error: null }); return; }
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const groups = companyId ? await companySearch(companyId, t) : companiesSearch(companies || [], t);
        setState((s) => (q.trim() === t ? { q: t, groups, loading: false, error: null } : s));
        setActive(0);
      } catch (e) {
        setState({ q: t, groups: [], loading: false, error: e });
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [q, companyId, companies]);

  const flat = useMemo(() => state.groups.flatMap((g) => g.items), [state.groups]);
  const go = (it) => { if (!it) return; location.hash = it.href; setOpen(false); setQ(""); input.current?.blur(); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); go(flat[active]); }
    else if (e.key === "Escape") { if (q) setQ(""); else input.current?.blur(); setOpen(false); }
  };
  const show = open && q.trim().length >= 2;
  let n = -1;
  return (
    <div className="gsearch" ref={root} role="search">
      <Icon name="search" />
      <input ref={input} type="search" value={q} placeholder={companyId ? (narrow ? "Пошук у кабінеті" : "Пошук ПН/РК, контрагентів, МД, звітів, кодів") : "Пошук компанії"}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
        role="combobox" aria-expanded={show} aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={show && flat[active] ? `${listId}-${active}` : undefined} aria-label="Пошук у кабінеті" />
      <kbd className="gsearch-kbd" aria-hidden="true">{isMac ? "⌘" : "Ctrl"} K</kbd>
      {show && (
        <div className="gsearch-pop" id={listId} role="listbox" aria-label="Результати пошуку">
          {state.loading && !state.groups.length && <p className="gsearch-note" role="status">Шукаємо…</p>}
          {state.error && <p className="gsearch-note error" role="alert">{state.error.message}</p>}
          {!state.loading && !state.error && !flat.length && <p className="gsearch-note">Нічого не знайдено за «{state.q}»</p>}
          {state.groups.filter((g) => g.items.length).map((g) => (
            <div key={g.title} role="group" aria-label={g.title}>
              <p className="gsearch-group">{g.title}</p>
              {g.items.map((it) => {
                n++;
                const i = n;
                return (
                  <div key={it.href} id={`${listId}-${i}`} role="option" aria-selected={i === active}
                    className={`gsearch-item${i === active ? " active" : ""}`} onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => { e.preventDefault(); go(it); }}>
                    <span className="gsearch-ico"><Icon name={g.icon} size={16} /></span>
                    <span className="gsearch-text"><span>{it.title}</span>{it.sub && <span className="muted small">{it.sub}</span>}</span>
                    {it.meta && <span className="muted small gsearch-meta">{it.meta}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Вузький екран — коротка підказка в полі пошуку
function useNarrow() {
  const mq = typeof matchMedia === "function" ? matchMedia("(max-width: 600px)") : null;
  const [v, setV] = useState(Boolean(mq?.matches));
  useEffect(() => {
    if (!mq) return;
    const on = () => setV(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return v;
}

function companiesSearch(list, t) {
  const s = t.toLowerCase();
  return [{ title: "Компанії", icon: "company", items: list.filter((c) => String(c.name || "").toLowerCase().includes(s) || c.edrpou.includes(s))
    .slice(0, 8).map((c) => ({ href: `#/c/${c.id}/overview`, title: shortName(c.name) || c.edrpou, sub: `ЄДРПОУ ${c.edrpou}` })) }];
}

async function companySearch(cid, t) {
  const base = `#/c/${cid}`;
  const [d, ref] = await Promise.all([
    api(`/api/companies/${cid}/search?q=${encodeURIComponent(t)}`),
    api(`/api/ref/search?q=${encodeURIComponent(t)}`).catch(() => ({ uktzed: [], dkpp: [] })),
  ]);
  return [
    { title: "Контрагенти", icon: "partners", items: d.partners.map((x) => ({ href: `${base}/partners/${x.tin}`, title: shortName(x.name) || x.tin, sub: `Код ${x.tin}`, meta: `${x.n} ПН/РК` })) },
    { title: "ПН/РК", icon: "invoice", items: d.invoices.map((x) => ({ href: `${base}/erpn/${x.direction}/${encodeURIComponent(x.nmr)}`,
      title: `${x.direction === "out" ? "Видана" : "Отримана"} ${x.doc_kind || "ПН"} № ${x.nmr}`, sub: shortName(x.party), meta: `${day(x.crtdate)} · ПДВ ${money(x.vat_sum)}` })) },
    { title: "Митні декларації", icon: "customs", items: d.customs.map((x) => ({ href: `${base}/customs/${encodeURIComponent(x.mrn)}`, title: x.mrn,
      sub: String(x.description || x.type || "").slice(0, 80), meta: anyDay(x.registered) })) },
    { title: "Звітність", icon: "reports", items: d.reports.map((x) => ({ href: `${base}/reports/${x.id}`, title: `${x.cdoc} · ${x.doc_name || ""}`.slice(0, 90),
      sub: [x.period_name, x.period_year].filter(Boolean).join(" "), meta: day(x.oper_date) })) },
    { title: "Коди УКТЗЕД / ДКПП", icon: "reference", items: [
      ...ref.uktzed.slice(0, 4).map((x) => ({ href: `${base}/reference/uktzed/${x.code}`, title: x.code, sub: x.short })),
      ...ref.dkpp.slice(0, 4).map((x) => ({ href: `${base}/reference/dkpp/${encodeURIComponent(x.code)}`, title: x.code, sub: x.name })),
    ] },
  ];
}
